/**
 * Web Audio synthesiser. Every sound in the game is generated here at runtime,
 * so the project ships with no audio files at all.
 *
 * The context is created lazily because browsers refuse to start audio before
 * the player interacts with the page.
 */

const clamp01 = (v) => Math.max(0, Math.min(1, v));

export function createSynth() {
  /** @type {AudioContext|null} */
  let ctx = null;
  let master = null;
  let sfxBus = null;
  let musicBus = null;
  let noiseBuffer = null;

  const state = {
    sfxOn: true,
    musicOn: true,
    sfxVolume: 0.55,
    musicVolume: 0.32,
    ready: false,
    /** True once a user gesture has let the audio clock run. */
    unlocked: false,
  };

  function ensure() {
    if (ctx) return ctx;
    const Ctor = window.AudioContext || window.webkitAudioContext;
    if (!Ctor) return null;

    ctx = new Ctor();
    master = ctx.createGain();
    master.gain.value = 0.9;
    master.connect(ctx.destination);

    sfxBus = ctx.createGain();
    sfxBus.gain.value = state.sfxOn ? state.sfxVolume : 0;
    sfxBus.connect(master);

    musicBus = ctx.createGain();
    musicBus.gain.value = state.musicOn ? state.musicVolume : 0;
    musicBus.connect(master);

    noiseBuffer = buildNoiseBuffer(ctx);
    state.ready = true;
    return ctx;
  }

  function buildNoiseBuffer(audioCtx) {
    const length = Math.floor(audioCtx.sampleRate * 0.6);
    const buffer = audioCtx.createBuffer(1, length, audioCtx.sampleRate);
    const data = buffer.getChannelData(0);
    // A crude LFSR gives that gritty NES-style noise instead of pure hiss.
    let reg = 0x7fff;
    for (let i = 0; i < length; i += 1) {
      const bit = ((reg >> 0) ^ (reg >> 1)) & 1;
      reg = (reg >> 1) | (bit << 14);
      data[i] = (reg & 1 ? 1 : -1) * 0.6;
    }
    return buffer;
  }

  /** Resume the context. Call from a real user gesture. */
  function unlock() {
    const audioCtx = ensure();
    if (!audioCtx) return Promise.resolve(false);
    const done = () => {
      state.unlocked = true;
      return true;
    };
    if (audioCtx.state === 'suspended') return audioCtx.resume().then(done);
    return Promise.resolve(done());
  }

  function now() {
    return ctx ? ctx.currentTime : 0;
  }

  /**
   * A single pitched blip.
   * @param {object} opts
   * @param {'square'|'triangle'|'sawtooth'|'sine'} [opts.type]
   * @param {number} opts.freq starting frequency
   * @param {number} [opts.freqEnd] frequency to glide to
   * @param {number} [opts.duration] seconds
   * @param {number} [opts.gain] peak gain 0..1
   * @param {number} [opts.attack] seconds
   * @param {number} [opts.delay] seconds from now
   * @param {AudioNode} [opts.bus]
   */
  function tone(opts) {
    const audioCtx = ensure();
    if (!audioCtx || !state.sfxOn) return;
    playTone(audioCtx, opts, opts.bus || sfxBus);
  }

  function playTone(audioCtx, opts, bus) {
    const {
      type = 'square',
      freq,
      freqEnd,
      duration = 0.12,
      gain = 0.5,
      attack = 0.004,
      delay = 0,
      detune = 0,
      curve = 'exp',
    } = opts;

    const start = audioCtx.currentTime + delay;
    const osc = audioCtx.createOscillator();
    const amp = audioCtx.createGain();

    osc.type = type;
    osc.detune.value = detune;
    osc.frequency.setValueAtTime(Math.max(20, freq), start);
    if (freqEnd && freqEnd !== freq) {
      const target = Math.max(20, freqEnd);
      if (curve === 'linear') osc.frequency.linearRampToValueAtTime(target, start + duration);
      else osc.frequency.exponentialRampToValueAtTime(target, start + duration);
    }

    const peak = clamp01(gain);
    amp.gain.setValueAtTime(0.0001, start);
    amp.gain.linearRampToValueAtTime(peak, start + attack);
    amp.gain.exponentialRampToValueAtTime(0.0001, start + duration);

    osc.connect(amp);
    amp.connect(bus);
    osc.start(start);
    osc.stop(start + duration + 0.02);
  }

  /**
   * Filtered noise burst, used for impacts and explosions.
   */
  function noise(opts = {}) {
    const audioCtx = ensure();
    if (!audioCtx || !state.sfxOn) return;
    const {
      duration = 0.18,
      gain = 0.4,
      filterFreq = 1800,
      filterEnd,
      filterType = 'bandpass',
      q = 1.1,
      delay = 0,
      bus = sfxBus,
    } = opts;

    const start = audioCtx.currentTime + delay;
    const src = audioCtx.createBufferSource();
    src.buffer = noiseBuffer;
    src.loop = true;

    const filter = audioCtx.createBiquadFilter();
    filter.type = filterType;
    filter.Q.value = q;
    filter.frequency.setValueAtTime(filterFreq, start);
    if (filterEnd) {
      filter.frequency.exponentialRampToValueAtTime(Math.max(40, filterEnd), start + duration);
    }

    const amp = audioCtx.createGain();
    amp.gain.setValueAtTime(clamp01(gain), start);
    amp.gain.exponentialRampToValueAtTime(0.0001, start + duration);

    src.connect(filter);
    filter.connect(amp);
    amp.connect(bus);
    src.start(start);
    src.stop(start + duration + 0.02);
  }

  /** Play a run of notes back to back. */
  function sequence(notes, opts = {}) {
    const audioCtx = ensure();
    if (!audioCtx || !state.sfxOn) return;
    const { step = 0.075, bus } = opts;
    notes.forEach((note, index) => {
      if (note == null) return;
      const config = typeof note === 'number' ? { freq: note } : note;
      playTone(
        audioCtx,
        { duration: step * 0.95, gain: 0.42, ...opts, ...config, delay: (opts.delay || 0) + index * step },
        bus || sfxBus,
      );
    });
  }

  function setSfxEnabled(on) {
    state.sfxOn = on;
    if (sfxBus && ctx) sfxBus.gain.setTargetAtTime(on ? state.sfxVolume : 0, ctx.currentTime, 0.02);
  }

  function setMusicEnabled(on) {
    state.musicOn = on;
    if (musicBus && ctx) {
      musicBus.gain.setTargetAtTime(on ? state.musicVolume : 0, ctx.currentTime, 0.05);
    }
  }

  return {
    state,
    ensure,
    unlock,
    now,
    tone,
    noise,
    sequence,
    setSfxEnabled,
    setMusicEnabled,
    /** Music voices route through their own bus so the toggle is independent. */
    get musicBus() {
      ensure();
      return musicBus;
    },
    get context() {
      return ctx;
    },
    playToneOn(opts, bus) {
      const audioCtx = ensure();
      if (!audioCtx) return;
      playTone(audioCtx, opts, bus || musicBus);
    },
    destroy() {
      if (ctx && ctx.state !== 'closed') {
        try {
          ctx.close();
        } catch {}
      }
      state.ready = false;
      state.unlocked = false;
      ctx = null;
    },
  };
}

/** Equal temperament helper: MIDI note number to frequency. */
export function midi(note) {
  return 440 * 2 ** ((note - 69) / 12);
}
